import React, { useCallback, useContext, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { connect } from "react-redux";
import { css } from "glamor";
import { spacing } from "../../library/theme";
import {
  createLessonInteractiveAnswer,
  createMockInteractiveAnswer,
} from "../../../processes/answerProccess";
import { getMock } from "../../../selectors/mockSelectors";
import { getFlattenedQuestionsFromMock } from "../../../selectors/questionSelectors";
import { getFlattenedLessonQuestions } from "../../../selectors/lessonQuestionSelectors";
import QuestionNavigationContainer from "../QuestionNavigationContainer";
import { InteractiveNavFooterContext } from "./InteractiveNavFooterContext";
import InteractiveHeader from "./InteractiveHeader";
import PromptBubble from "./PromptBubble";
import AlternativesList from "./AlternativesList";
import AnswerFeedbackCard from "./AnswerFeedbackCard";
import ChoicesSummary from "./ChoicesSummary";
import {
  buildAnswersByPrompt,
  buildConversationPath,
  getCurrentConversationEntry,
  getStartPrompt,
  seededShuffle,
  sortPromptsByPosition,
  toAlternativeMap,
  toPromptMap,
} from "./questionInteractiveUtils";

const finalFeedbackWrapperClass = css({
  marginTop: spacing.l,
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
});

function QuestionInteractive({
  question,
  lesson,
  readOnly,
  isExam,
  isFinished,
  mockId,
  courseId,
  allAnswers,
  totalQuestions,
  questionNumber,
  routePrefix,
  dispatch,
}) {
  const prompts = useMemo(
    () => sortPromptsByPosition(question?.meta?.questionPrompts || []),
    [question?.meta?.questionPrompts]
  );

  const totalSteps = useMemo(() => {
    const fromApi = question?.interactiveLevelsCount;
    if (typeof fromApi === "number" && fromApi > 0) {
      return fromApi;
    }
    const numericLevels = prompts
      .map((p) => (typeof p.level === "number" ? p.level : null))
      .filter((n) => n != null);
    if (numericLevels.length > 0) {
      return Math.max(...numericLevels) + 1;
    }
    return prompts.length;
  }, [question?.interactiveLevelsCount, prompts]);

  const promptsById = useMemo(() => toPromptMap(prompts), [prompts]);
  const startPrompt = useMemo(() => getStartPrompt(prompts), [prompts]);

  const answersByPrompt = useMemo(
    () => buildAnswersByPrompt(allAnswers, question?.id),
    [allAnswers, question?.id]
  );
  const conversationPath = useMemo(
    () =>
      buildConversationPath({
        startPrompt,
        promptsById,
        answersByPrompt,
      }),
    [startPrompt, promptsById, answersByPrompt]
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingAlternativeId, setPendingAlternativeId] = useState(null);
  const [eliminatedIdsByPrompt, setEliminatedIdsByPrompt] = useState({});

  const revealResult = readOnly || !isExam || !!isFinished;
  const isSearchReadOnlyNoMock = readOnly && mockId == null;

  const selectedAlternativesByPrompt = useMemo(() => {
    const mapped = {};
    conversationPath.forEach(({ prompt, answer }) => {
      if (!prompt?.id || !answer?.questionPromptAlternativeId) return;
      const alternativesById = toAlternativeMap(prompt);
      mapped[prompt.id] = alternativesById[answer.questionPromptAlternativeId] || null;
    });
    return mapped;
  }, [conversationPath]);

  const currentEntry = useMemo(
    () => getCurrentConversationEntry(conversationPath),
    [conversationPath]
  );
  const currentPrompt = currentEntry?.prompt || null;
  const currentAnswer = currentEntry?.answer || null;
  const shuffleSeedBase = Number(mockId || lesson?.id || 0);
  const currentAlternatives = useMemo(() => {
    const alts = currentPrompt?.alternatives || [];
    const seed = shuffleSeedBase + Number(currentPrompt?.id || 0);
    return seededShuffle(alts, seed);
  }, [currentPrompt, shuffleSeedBase]);

  const isQuestionCompleted = useMemo(() => {
    if (conversationPath.length === 0) return false;
    const lastAnswered = [...conversationPath].reverse().find((e) => e.answer);
    if (!lastAnswered) return false;
    return !!lastAnswered.prompt?.isFinal;
  }, [conversationPath]);

  const answeredEntries = useMemo(
    () => conversationPath.filter((e) => e.answer != null),
    [conversationPath]
  );

  const navFooterEl = useContext(InteractiveNavFooterContext);
  const showFinalFeedback = isQuestionCompleted && revealResult;

  const submitAnswer = useCallback(
    (alternative) => {
      if (readOnly || !currentPrompt?.id || currentAnswer || !alternative?.id) return;

      setIsSubmitting(true);

      const request = lesson
        ? createLessonInteractiveAnswer(
            dispatch,
            lesson,
            question.id,
            currentPrompt.id,
            alternative.id
          )
        : createMockInteractiveAnswer(
            dispatch,
            courseId,
            mockId,
            question.id,
            currentPrompt.id,
            alternative.id
          );

      request.finally(() => {
        setIsSubmitting(false);
      });
    },
    [readOnly, currentPrompt, currentAnswer, lesson, dispatch, question?.id, courseId, mockId]
  );

  const handleAlternativeClick = useCallback(
    (alternative) => {
      if (readOnly || currentAnswer || isSubmitting) return;

      if (isExam) {
        submitAnswer(alternative);
      } else {
        setPendingAlternativeId(alternative?.id);
      }
    },
    [readOnly, currentAnswer, isExam, isSubmitting, submitAnswer]
  );

  const handleSubmitPending = useCallback(() => {
    if (!pendingAlternativeId || isSubmitting) return;
    const alt = currentAlternatives.find(
      (a) => Number(a.id) === Number(pendingAlternativeId)
    );
    if (alt) {
      submitAnswer(alt);
      setPendingAlternativeId(null);
    }
  }, [pendingAlternativeId, currentAlternatives, isSubmitting, submitAnswer]);

  const handleEliminate = useCallback((promptId, alternativeId) => {
    setEliminatedIdsByPrompt((prev) => {
      const current = new Set(prev[promptId] || []);
      if (current.has(alternativeId)) {
        current.delete(alternativeId);
      } else {
        current.add(alternativeId);
      }
      return { ...prev, [promptId]: new Set(current) };
    });
    setPendingAlternativeId((prev) => {
      if (prev != null && Number(prev) === Number(alternativeId)) return null;
      return prev;
    });
  }, []);

  // When the flow is completed but we're in exam mode and still not finished,
  // we hide intermediate feedback (neutral) per design: only the final card is shown.
  const showIntermediateFeedback = revealResult;

  if (!question) return null;

  const currentStep = conversationPath.length > 0 ? conversationPath.length : 1;
  const senderName = question?.promptSenderName || "Cliente";
  const senderObservations = question?.promptSenderObservations || null;

  return (
    <div
      data-testid="question-interactive"
      data-question-id={question?.id}
      data-completed={isQuestionCompleted ? "true" : "false"}
      data-current-step={currentStep}
      data-total-steps={totalSteps}
    >
      <InteractiveHeader
        question={question}
        questionNumber={questionNumber}
        totalQuestions={totalQuestions}
        currentStep={currentStep}
        totalSteps={totalSteps}
      />

      {isSearchReadOnlyNoMock ? (
        prompts.map((prompt, index) => {
          const seed = Number(question?.id || 0) + Number(prompt?.id || 0);
          return (
            <div key={prompt?.id || index}>
              <PromptBubble
                senderName={senderName}
                senderObservations={senderObservations}
                content={prompt?.content}
                stepNumber={index + 1}
                showSenderInfo={index === 0}
              />
              <AlternativesList
                alternatives={seededShuffle(prompt?.alternatives || [], seed)}
                selectedAlternativeId={null}
                disabled
                showJustifications
              />
            </div>
          );
        })
      ) : (
        conversationPath.map(({ prompt, answer }, index) => {
          const selectedAlternativeForPrompt = selectedAlternativesByPrompt[prompt?.id];
          const isLastEntry = index === conversationPath.length - 1;
          const showAlternativesForEntry = isLastEntry && !answer && !readOnly;
          const eliminatedForPrompt = eliminatedIdsByPrompt[prompt?.id] || new Set();
          const isPendingEliminated =
            pendingAlternativeId != null &&
            eliminatedForPrompt.has(pendingAlternativeId);
          const effectivePendingId = isPendingEliminated ? null : pendingAlternativeId;

          return (
            <div key={prompt?.id || index}>
              <PromptBubble
                senderName={senderName}
                senderObservations={senderObservations}
                content={prompt?.content}
                stepNumber={index + 1}
                showSenderInfo={index === 0}
                isActive={isLastEntry && !answer}
              />

              {answer && showIntermediateFeedback && (
                <AnswerFeedbackCard
                  selectedAlternative={selectedAlternativeForPrompt}
                  isCorrect={answer?.correct === true}
                  revealResult={revealResult}
                />
              )}

              {showAlternativesForEntry && (
                <AlternativesList
                  alternatives={currentAlternatives}
                  selectedAlternativeId={effectivePendingId}
                  eliminatedIds={eliminatedForPrompt}
                  disabled={isSubmitting}
                  onSelect={handleAlternativeClick}
                  onEliminate={(altId) => handleEliminate(prompt?.id, altId)}
                />
              )}
            </div>
          );
        })
      )}

      {showFinalFeedback && (
        <div className={finalFeedbackWrapperClass}>
          <ChoicesSummary
            question={question}
            answeredEntries={answeredEntries}
          />
        </div>
      )}

      {(() => {
        const nav = (
          <QuestionNavigationContainer
            lesson={lesson}
            routePrefix={routePrefix}
            onInteractiveAnswer={handleSubmitPending}
            isInteractiveAnswerReady={pendingAlternativeId != null}
            isInteractiveAnswering={isSubmitting}
          />
        );
        return navFooterEl ? createPortal(nav, navFooterEl) : nav;
      })()}
    </div>
  );
}

function mapStateToProps(state, ownProps) {
  const { question, lesson } = ownProps;
  const mock = getMock(state);
  const mockId = mock?.id;
  const isExam = !lesson && !!mock?.exam;
  const isFinished = !!mock?.finishedAt;
  const answers = Object.values(state.entities?.answers || {});
  const allAnswers = answers.filter((answer) => {
    if (!answer) return false;
    if (Number(answer.questionId) !== Number(question?.id)) return false;

    if (lesson?.id) {
      return Number(answer.lessonId) === Number(lesson.id);
    }

    if (mockId == null) return false;

    return Number(answer.mockId) === Number(mockId);
  });

  const flattenedQuestions = lesson
    ? getFlattenedLessonQuestions(state)
    : getFlattenedQuestionsFromMock(state);
  const currentIndex = lesson ? state.lesson.currentQuestionIndex : state.mock.currentIndex;
  const questionNumber = Number(currentIndex || 0) + 1;
  const totalQuestions = flattenedQuestions.length;

  return {
    mockId,
    isExam,
    isFinished,
    courseId: mock?.courseId,
    allAnswers,
    questionNumber,
    totalQuestions,
  };
}

export default connect(mapStateToProps)(QuestionInteractive);
